import React, { useMemo } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { GRAY_LIGHT_SECONDARY } from 'constants/colors';
import { useTranslation } from 'react-i18next';

type PlanningCycle = {
  enabled: boolean;
  recurrenceRule: {
    start: string;
    periodType: 'MONTH' | 'WEEK';
    interval: number;
    dayInWeekSet: string[];
    dayOfMonth: number | null;
    setPosition: number | null;
  };
  cycleWindow: number;
  rosterState: 'PUBLISHED' | 'VISIBLE';
  rounds: {
    startIndex: number;
    endIndex: number;
    autoConnect: boolean;
  }[];
};

type Props = {
  planningCycle: PlanningCycle | null;
  onEditClick: () => void;
  isChecked: boolean;
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const PlanningCycleInfo: React.FC<Props> = ({
  planningCycle,
  onEditClick,
  isChecked
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'planningCycle' });

  const text = useMemo(() => {
    if (planningCycle && planningCycle.enabled) {
      const { start, interval, periodType, dayOfMonth, setPosition } =
        planningCycle.recurrenceRule;
      const { cycleWindow } = planningCycle;

      let calendarText = '';
      const isSingular = interval === 1;
      const typeKey = isSingular
        ? periodType.toLowerCase()
        : `${periodType.toLowerCase()}_plural`;
      const typeText = isSingular
        ? t(periodType.toLowerCase()) // "maand" or "week"
        : t(`${periodType.toLowerCase()}_plural`, { count: interval });

      const adjustedDate = dayjs(start).add(1, 'day').locale('en');
      const date = adjustedDate.format('DD-MM-YYYY');
      const weekdayName = adjustedDate.format('dddd').toLocaleLowerCase();
      const weekday = t(weekdayName);

      // Map setPosition to ordinal text
      const ordinalMap: { [key: number]: string } = {
        1: t('first'),
        2: t('second'),
        3: t('third'),
        4: t('fourth'),
        5: t('last')
      };
      const setPositionText = ordinalMap[setPosition || 1];

      if (typeKey.startsWith('month')) {
        if (dayOfMonth) {
          calendarText = t('onDay', { dayOfMonth });
        } else {
          calendarText = t('onTheNthWeekday', {
            setPosition: setPositionText,
            weekday
          });
        }
      } else {
        calendarText = t('repeatOnWeekday', { weekday });
      }

      const repeatEveryText = isSingular
        ? t('repeatEverySingular', { type: typeText, calendarText, date })
        : t('repeatEvery', { interval, type: typeText, calendarText, date });

      const action =
        planningCycle.rosterState === 'PUBLISHED'
          ? t('publishes')
          : t('makeVisible');

      const cycleWindowTypeKey =
        cycleWindow === 1
          ? periodType.toLowerCase()
          : `${periodType.toLowerCase()}_plural`;
      const cycleWindowTypeText = t(cycleWindowTypeKey, {
        count: cycleWindow
      });

      const rosterStateActionText = t('rosterStateAction', {
        action,
        cycleWindow,
        type: cycleWindowTypeText,
        count: cycleWindow
      });

      let resultText = `${repeatEveryText} <br /> ${rosterStateActionText}`;

      planningCycle.rounds.forEach((round, i) => {
        const autoConnectText = round.autoConnect ? t('withAutoConnect') : '';
        const roundText = t('roundDetails', {
          roundNumber: i + 1,
          startIndex: round.startIndex,
          endIndex: round.endIndex,
          autoConnect: autoConnectText
        });
        resultText += `<br /> ${roundText}`;
      });

      return resultText;
    }
    return '';
  }, [planningCycle, t]);

  return (
    <>
      {planningCycle && planningCycle.enabled && isChecked && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mb={4}
          p={1}
          borderRadius={2}
          bgcolor={GRAY_LIGHT_SECONDARY}
        >
          <IconButton onClick={onEditClick} size="small">
            <EditIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="body2"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </Box>
      )}
    </>
  );
};

export default PlanningCycleInfo;
